/**
 * Mock insight service — backed by the in-memory `mockInsights` fixtures.
 *
 * Mutations operate on a module-local clone so dismiss/apply/generate feel live
 * across calls within a session, mirroring the real service's behaviour.
 */

import { mockInsights } from '@/data/mockInsights'
import { INSIGHT_TYPE_LABELS } from '@/types/insight.types'
import { mockDelay } from '@/services/factory'
import type { InsightService } from './insights.service'
import type {
  Insight,
  InsightListFilters,
  InsightType,
  GenerateInsightsInput,
  GenerateInsightsResult,
} from '@/types/insight.types'

const GENERATED_TYPES: InsightType[] = ['tax_saving', 'deduction', 'optimization']

// Session-local store so mutations persist between calls during a page session.
let store: Insight[] = mockInsights.map((i) => ({ ...i }))

function applyFilters(items: Insight[], filters?: InsightListFilters): Insight[] {
  return items.filter((i) => {
    if (filters?.status && i.status !== filters.status) return false
    if (filters?.insightType && i.insightType !== filters.insightType) return false
    if (filters?.customerId && i.customerId !== filters.customerId) return false
    return true
  })
}

function findOrThrow(id: string): Insight {
  const found = store.find((i) => i.id === id)
  if (!found) throw new Error('Insight not found')
  return found
}

export function createMockInsightService(): InsightService {
  return {
    async list(filters?: InsightListFilters): Promise<Insight[]> {
      await mockDelay(300)
      return applyFilters(store, filters).map((i) => ({ ...i }))
    },

    async listForCustomer(
      customerId: string,
      filters?: InsightListFilters
    ): Promise<Insight[]> {
      await mockDelay(300)
      return applyFilters(store, { ...filters, customerId }).map((i) => ({ ...i }))
    },

    async dismiss(id: string): Promise<Insight> {
      await mockDelay(250)
      const insight = findOrThrow(id)
      insight.status = 'dismissed'
      insight.updatedAt = new Date().toISOString()
      return { ...insight }
    },

    async apply(id: string): Promise<Insight> {
      await mockDelay(250)
      const insight = findOrThrow(id)
      insight.status = 'applied'
      insight.updatedAt = new Date().toISOString()
      return { ...insight }
    },

    async generate(input: GenerateInsightsInput): Promise<GenerateInsightsResult> {
      await mockDelay(700)
      const types = input.insightTypes?.length ? input.insightTypes : GENERATED_TYPES
      const nowIso = new Date().toISOString()
      const created: Insight[] = types.map((type, idx) => ({
        id: `ins-gen-${Date.now()}-${idx}`,
        customerId: input.customerId,
        insightType: type,
        title: `New ${INSIGHT_TYPE_LABELS[type]} opportunity`,
        description: `Freshly generated ${INSIGHT_TYPE_LABELS[
          type
        ].toLowerCase()} insight for this customer based on the latest profile data.`,
        recommendations: ['Review the recommendation with the client'],
        confidenceScore: 0.8,
        status: 'active',
        createdAt: nowIso,
        updatedAt: nowIso,
      }))
      store = [...created, ...store]
      return {
        customerId: input.customerId,
        insightsGenerated: created.length,
        insightIds: created.map((i) => i.id),
        message: `Successfully generated ${created.length} insights for customer ${input.customerId}`,
      }
    },
  }
}
